import { createContext, useContext, type ReactNode } from 'react';

import { useSoluuDevice } from '../hooks/useSoluuDevice.js';

/**
 * Owns the one connected Soluu for the whole app.
 *
 * Before this provider, `HomePage` called `useSoluuDevice()` itself. `AppShell`
 * swaps pages by route, so navigating away unmounted the hook and dropped the
 * device — and any sibling route that called the hook again would open a
 * *second* browser chooser. Spec 0002 adds sibling routes, so the device state
 * is lifted here and every page reads it through `useDevice()`.
 *
 * One device at a time, per spec 0001 §"Out of Scope".
 *
 * Mounted inside `AttemptIdProvider` and `DiagnosticsProvider`: `useSoluuDevice`
 * consumes both, funnelling ble-core's debug events into the diagnostics log
 * tagged with the in-flight attempt id.
 */
export type DeviceContextValue = ReturnType<typeof useSoluuDevice>;

const DeviceContext = createContext<DeviceContextValue | null>(null);

export function DeviceProvider({ children }: { children: ReactNode }) {
  // `useSoluuDevice` already returns a fresh object each render, and its
  // members change together with the connection state, so memoizing here would
  // only add a dependency list to keep in sync with the hook's shape.
  const device = useSoluuDevice();
  return <DeviceContext.Provider value={device}>{children}</DeviceContext.Provider>;
}

export function useDevice(): DeviceContextValue {
  const ctx = useContext(DeviceContext);
  if (!ctx) throw new Error('useDevice must be used inside <DeviceProvider>');
  return ctx;
}
